import { AsyncSelectInput, AsyncSelectInputOption } from '@/components/Forms/AsyncSelect';
import { sortByLabel } from '@/components/Pricing/Mappings/util';
import { debounceAsync } from '@/helpers/debounceAsync';
import { useLazySearchOrganizationsQuery } from '@/services/organizations';
import { Box, FormikSelectInputNative, FormikTextInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';

import {
  CreatePayeeTransactionPayload,
  GeneralLedgerCategory,
  GeneralLedgerCategoryMap,
  PayeePayoutEvent,
  PayeePayoutEventMap,
} from 'types';

export interface PayeeTransactionFormValues {
  payee: AsyncSelectInputOption | '';
  event: string;
  generalLedgerCategory: string;
  amount: number | '';
  description: string;
}

export const emptyPayeeTransactionValues: Omit<PayeeTransactionFormValues, 'payee'> = {
  event: '',
  generalLedgerCategory: '',
  amount: '',
  description: '',
};

export const payeeSelectionSchema = yup
  .mixed()
  .test('hasOrganization', 'Organization is a required field.', (value) =>
    Boolean((value as AsyncSelectInputOption | undefined)?.value),
  );

export const payeeTransactionFieldsSchema = {
  event: yup.string().required('Payout Event is a required field.'),
  generalLedgerCategory: yup.string().required('General Ledger Category is a required field.'),
  amount: yup
    .number()
    .typeError('Amount must be a number.')
    .required('Amount is a required field.')
    .test('isNonZero', 'Amount must not be zero', (value) => value !== 0),
  description: yup.string().required('Description is a required field.'),
};

export const payeeTransactionSchema = yup.object({
  payee: payeeSelectionSchema,
  ...payeeTransactionFieldsSchema,
});

export const toCreatePayeeTransactionPayload = (
  values: PayeeTransactionFormValues,
): CreatePayeeTransactionPayload => ({
  payeeExternalId: (values.payee as AsyncSelectInputOption).value,
  event: values.event as never,
  generalLedgerCategory: values.generalLedgerCategory as never,
  amount: Number(values.amount),
  description: values.description,
});

const usePayeeTransactionOptions = () => {
  const { safeHarborOrgTransactions = false } = useFlags();

  return useMemo(
    () => ({
      eventOptions: Object.entries(PayeePayoutEventMap)
        .filter(([value]) => safeHarborOrgTransactions || value !== PayeePayoutEvent.shipping)
        .map(([value, label]) => ({ value, label })),
      generalLedgerCategoryOptions: Object.entries(GeneralLedgerCategoryMap)
        .filter(([value]) => safeHarborOrgTransactions || value !== GeneralLedgerCategory.safeHarbor)
        .map(([value, label]) => ({ value, label })),
    }),
    [safeHarborOrgTransactions],
  );
};

export const hasSelectedPayee = (values: { payee?: AsyncSelectInputOption | '' }) =>
  Boolean((values.payee as AsyncSelectInputOption | undefined)?.value);

interface PayeeOrganizationSelectProps {
  onSelected?: (option: AsyncSelectInputOption | '') => void;
  className?: string;
}

export const PayeeOrganizationSelect = ({ onSelected, className }: PayeeOrganizationSelectProps) => {
  const formik = useFormikContext<PayeeTransactionFormValues>();
  const [triggerSearch] = useLazySearchOrganizationsQuery();

  const searchOrganizations = useCallback(
    async (searchText: string) => {
      const results = await triggerSearch({ searchTerm: searchText, useRootOrganization: true });

      return results?.data?.flat
        ?.map((org) => ({ value: org.alias, label: `${org.name} (${org.alias})` }))
        .toSorted(sortByLabel);
    },
    [triggerSearch],
  );

  const debouncedSearch = useMemo(() => debounceAsync(searchOrganizations, 400), [searchOrganizations]);

  const handlePayeeChange = useCallback(
    (event: { target: { value: AsyncSelectInputOption | '' } }) => {
      void formik.setFieldValue('payee', event.target.value);
      onSelected?.(event.target.value);
    },
    [formik, onSelected],
  );

  return (
    <AsyncSelectInput
      className={className}
      id="payee"
      name="payee"
      label="Payee"
      placeholder="Search for an organization"
      loadOptions={debouncedSearch}
      value={formik.values.payee}
      onChange={handlePayeeChange}
      onBlur={formik.handleBlur}
      isRequired
      isClearable
      isDisabled={formik.isSubmitting}
      noOptionsMessage={() => 'Search for an organization'}
      error={formik.touched.payee && (formik.errors.payee as string)}
      menuPortalTarget={document.body}
    />
  );
};

export const PayeeTransactionFields = ({ className }: { className?: string }) => {
  const { isSubmitting } = useFormikContext<PayeeTransactionFormValues>();
  const { eventOptions, generalLedgerCategoryOptions } = usePayeeTransactionOptions();

  return (
    <Box className={className} childGap={{ base: 'lg', desktop: 'xl' }}>
      <Field
        label="Payout Event"
        name="event"
        id="event"
        component={FormikSelectInputNative}
        options={eventOptions}
        isRequired
        isDisabled={isSubmitting}
      />
      <Field
        label="General Ledger Category"
        name="generalLedgerCategory"
        id="generalLedgerCategory"
        component={FormikSelectInputNative}
        options={generalLedgerCategoryOptions}
        isRequired
        isDisabled={isSubmitting}
      />
      <Field
        type="number"
        label="Amount"
        name="amount"
        id="amount"
        component={FormikTextInput}
        isRequired
        isDisabled={isSubmitting}
        helpText="Negative for a deduction. Zero is not allowed."
      />
      <Field
        type="text"
        label="Description"
        name="description"
        id="description"
        component={FormikTextInput}
        isRequired
        isDisabled={isSubmitting}
      />
    </Box>
  );
};
